// /programs/removebg — IOPaint 이식: 배경 제거(RMBG-1.4) + 브러시/클릭 객체 지우개(big-lama + SAM2)
// + 확장 플러그인(AnimeSeg·RealESRGAN 업스케일·GFPGAN/RestoreFormer 얼굴 보정).
// 내부 iopaint 컨테이너를 Tim Box API(/removebg/*)가 프록시 — 별도 포트/프로그램 없음.
import { useEffect, useRef, useState } from "react";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";

const MAX_SIDE = 2500;          // CPU 추론 속도를 위해 긴 변 제한 (초과 시 자동 축소)
const MAX_UPSCALE_OUT = 6000;   // 업스케일 결과 긴 변 제한 (CPU 시간·메모리 보호)
const ACCEPT = /\.(png|jpe?g|webp|bmp)$/i;
const FILL = "#ff2e63";         // 선택 영역 표시색 (비비드 핑크레드)

// 투명 배경 표시용 체커보드
const CHECKER: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(45deg,#d6d6dd 25%,transparent 25%),linear-gradient(-45deg,#d6d6dd 25%,transparent 25%)," +
    "linear-gradient(45deg,transparent 75%,#d6d6dd 75%),linear-gradient(-45deg,transparent 75%,#d6d6dd 75%)",
  backgroundSize: "20px 20px",
  backgroundPosition: "0 0,0 10px,10px -10px,-10px 0",
  backgroundColor: "#f3f3f6",
};

/** dataURL → 순수 base64 */
function b64Of(dataUrl: string) { return dataUrl.slice(dataUrl.indexOf(",") + 1); }

/** 바이트 → 사람이 읽는 용량 문자열 */
function fmtBytes(n: number) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Blob → HTMLImageElement */
function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = reject;
    img.src = url;
  });
}

/** 투명(알파≈0) 가장자리를 잘라낸 캔버스 반환 — 여백 제거. 내용이 전부 채워졌으면 원본 그대로. */
function trimTransparent(src: HTMLCanvasElement): HTMLCanvasElement {
  const w = src.width, h = src.height;
  const data = src.getContext("2d")!.getImageData(0, 0, w, h).data;
  const ALPHA = 8;
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > ALPHA) {
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX || maxY < minY) return src;            // 전부 투명 → 원본
  const sw = maxX - minX + 1, sh = maxY - minY + 1;
  if (sw === w && sh === h) return src;                   // 여백 없음 → 원본
  const out = document.createElement("canvas");
  out.width = sw; out.height = sh;
  out.getContext("2d")!.drawImage(src, minX, minY, sw, sh, 0, 0, sw, sh);
  return out;
}

/** 캔버스를 PNG로 다운로드 (완료될 때 resolve). */
function downloadCanvas(canvas: HTMLCanvasElement, name: string): Promise<void> {
  return new Promise(resolve => {
    canvas.toBlob(blob => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = name;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
      resolve();
    }, "image/png");
  });
}

export default function RemoveBgPage() {
  const [hasImage, setHasImage] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);   // "배경 제거 중…" 등
  const [brush, setBrush] = useState(36);
  const [maskDirty, setMaskDirty] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [svcReady, setSvcReady] = useState<boolean | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState("image");
  const [segMode, setSegMode] = useState(false);            // 🎯 클릭 개체 선택 모드
  const [plugins, setPlugins] = useState<string[] | null>(null); // 서버 활성 플러그인
  const [upScale, setUpScale] = useState<"2" | "4">("2");
  const [faceModel, setFaceModel] = useState<"GFPGAN" | "RestoreFormer">("GFPGAN");
  const [imgMeta, setImgMeta] = useState<{ w: number; h: number; bytes: number } | null>(null); // 좌측 하단 해상도·용량
  const [cropMode, setCropMode] = useState(false);          // ⬚ 영역 자르기 모드
  type Rect = { x: number; y: number; w: number; h: number };
  const [cropRects, setCropRects] = useState<Rect[]>([]);   // 확정된 여러 영역 (캔버스 px)
  const [cropDraft, setCropDraft] = useState<Rect | null>(null); // 드래그 중인 영역

  const imgCanvas = useRef<HTMLCanvasElement>(null);   // 작업 이미지
  const maskCanvas = useRef<HTMLCanvasElement>(null);  // 전송용 마스크 (white on black)
  const viewMask = useRef<HTMLCanvasElement>(null);    // 화면 표시용 오버레이
  const alphaMask = useRef<HTMLCanvasElement | null>(null); // 표시 합성용 (투명 바탕 + 불투명 흰 선)
  const tintTmp = useRef<HTMLCanvasElement | null>(null);   // 표시 합성용 임시
  const cursorRef = useRef<HTMLDivElement>(null);      // 브러시 커서 링
  const fileInput = useRef<HTMLInputElement>(null);
  const history = useRef<ImageData[]>([]);   // 되돌리기 스택
  const redoStack = useRef<ImageData[]>([]); // 되돌리기 취소(redo) 스택
  const drawing = useRef(false);
  const lastPt = useRef<{ x: number; y: number } | null>(null);
  const strokeAnchor = useRef<{ x: number; y: number } | null>(null); // Shift 직선 그리기 기준점(스트로크 시작점)
  const segClicks = useRef<[number, number, number][]>([]); // [x,y,1=포함|0=제외]
  const rafPending = useRef(false);
  const cropStart = useRef<{ x: number; y: number } | null>(null); // 크롭 드래그 시작점

  // ── 확대/축소·이동(뷰 변환) ────────────────────────────────────────────────
  // Ctrl+드래그(상하) = 확대/축소, Ctrl+휠 = 커서 기준 확대/축소, 중간버튼/Ctrl+Shift+드래그 = 이동.
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [gesturing, setGesturing] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);  // transform 대상(캔버스 스택)
  const bodyRef = useRef<HTMLDivElement>(null);    // 휠 리스너 부착 대상
  const viewRef = useRef(view);                    // 이벤트 핸들러 최신 뷰 참조
  viewRef.current = view;
  // 진행 중 제스처: zoom=커서고정 확대, pan=화면좌표 평행이동
  const gesture = useRef<null | {
    mode: "zoom" | "pan"; startX: number; startY: number;
    scale0: number; x0: number; y0: number;
    cx: number; cy: number; Lx: number; Ly: number; ax: number; ay: number;
  }>(null);
  const clampScale = (s: number) => Math.min(8, Math.max(1, s));

  useEffect(() => {
    api.get<{ ok: boolean }>("/removebg/health").then(r => setSvcReady(r.data.ok)).catch(() => setSvcReady(false));
    api.get<{ plugins: string[] }>("/removebg/plugins").then(r => setPlugins(r.data.plugins)).catch(() => setPlugins(null));
  }, []);

  // 플러그인 활성 여부 — 목록을 못 받았으면(구버전 API) 일단 활성으로 두고 호출 시 에러로 안내
  const has = (name: string) => plugins === null || plugins.includes(name);
  const offTitle = (name: string) => (has(name) ? undefined : "서버에서 비활성 — iopaint 컨테이너 재빌드 필요");

  // ── 캔버스 준비 ─────────────────────────────────────────────────────────────
  const ensureAux = (w: number, h: number) => {
    if (!alphaMask.current) alphaMask.current = document.createElement("canvas");
    if (!tintTmp.current) tintTmp.current = document.createElement("canvas");
    for (const c of [alphaMask.current, tintTmp.current]) { c.width = w; c.height = h; }
  };

  // 현재 작업 캔버스의 해상도·용량을 좌측 하단 배지에 반영.
  // knownBytes를 주면 그 값을 쓰고(업로드 직후 원본 파일 크기), 없으면 캔버스 PNG로 추정.
  const refreshMeta = (knownBytes?: number) => {
    const c = imgCanvas.current;
    if (!c) return;
    const w = c.width, h = c.height;
    if (knownBytes != null) { setImgMeta({ w, h, bytes: knownBytes }); return; }
    c.toBlob(b => setImgMeta({ w, h, bytes: b?.size ?? 0 }), "image/png");
  };

  const loadFile = async (f: File) => {
    if (!ACCEPT.test(f.name)) { toast.error("지원하지 않는 형식", "png · jpg · webp · bmp"); return; }
    const img = await blobToImage(f);
    let w = img.naturalWidth, h = img.naturalHeight;
    if (Math.max(w, h) > MAX_SIDE) {
      const s = MAX_SIDE / Math.max(w, h);
      w = Math.round(w * s); h = Math.round(h * s);
      toast.info("이미지 축소됨", `처리 속도를 위해 ${MAX_SIDE}px로 줄였습니다`);
    }
    for (const ref of [imgCanvas, maskCanvas, viewMask]) {
      const c = ref.current!; c.width = w; c.height = h;
    }
    ensureAux(w, h);
    const ctx = imgCanvas.current!.getContext("2d")!;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    clearMask();
    history.current = [];
    redoStack.current = [];
    setCanUndo(false);
    setCanRedo(false);
    setFileName(f.name.replace(/\.[^.]+$/, ""));
    setHasImage(true);
    refreshMeta(f.size);
    setView({ scale: 1, x: 0, y: 0 });
  };

  const clearMask = () => {
    const m = maskCanvas.current!, v = viewMask.current!;
    m.getContext("2d")!.fillStyle = "#000";
    m.getContext("2d")!.fillRect(0, 0, m.width, m.height);
    alphaMask.current?.getContext("2d")!.clearRect(0, 0, m.width, m.height);
    v.getContext("2d")!.clearRect(0, 0, v.width, v.height);
    segClicks.current = [];
    setMaskDirty(false);
  };

  // 현재 캔버스 상태를 ImageData로 캡처
  const snapshot = () => {
    const c = imgCanvas.current!;
    return c.getContext("2d")!.getImageData(0, 0, c.width, c.height);
  };

  // 스냅샷을 캔버스에 복원 (크기 변화 포함)
  const restoreSnapshot = (snap: ImageData) => {
    for (const ref of [imgCanvas, maskCanvas, viewMask]) {
      ref.current!.width = snap.width; ref.current!.height = snap.height;
    }
    ensureAux(snap.width, snap.height);
    imgCanvas.current!.getContext("2d")!.putImageData(snap, 0, 0);
    clearMask();
    refreshMeta();
  };

  const pushHistory = () => {
    history.current.push(snapshot());
    if (history.current.length > 10) history.current.shift();  // 메모리 보호
    redoStack.current = [];   // 새 편집이 들어오면 redo 스택은 무효화
    setCanRedo(false);
    setCanUndo(true);
  };

  const undo = () => {
    const prev = history.current.pop();
    if (!prev) return;
    redoStack.current.push(snapshot());           // 현재 상태를 redo 스택에 보관
    if (redoStack.current.length > 10) redoStack.current.shift();
    setCanRedo(true);
    restoreSnapshot(prev);
    setCanUndo(history.current.length > 0);
  };

  const redo = () => {
    const next = redoStack.current.pop();
    if (!next) return;
    history.current.push(snapshot());             // 현재 상태를 undo 스택에 보관
    if (history.current.length > 10) history.current.shift();
    setCanUndo(true);
    restoreSnapshot(next);
    setCanRedo(redoStack.current.length > 0);
  };

  // ── 키보드: Backspace=되돌리기, [ ] =브러시 크기 (입력창에선 일반 동작 유지) ──────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return; // 타이핑 방해 금지
      if (e.key === "Backspace") {
        if (history.current.length > 0) { e.preventDefault(); undo(); }
      } else if (e.key === "[" || e.key === "]") {
        e.preventDefault();
        const d = e.key === "]" ? 4 : -4;               // ] 크게 · [ 작게
        setBrush(b => Math.min(120, Math.max(8, b + d))); // 슬라이더와 동일한 8~120px 범위
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 브라우저 뒤로가기 = 화면 안에서 되돌리기 (편집 중 실수로 페이지 이탈 방지) ──
  // 이미지가 있을 때 트랩 엔트리를 심고, 뒤로가기 시 undo가 남아있으면 한 단계 되돌린 뒤
  // 다시 무장한다. 되돌릴 게 없으면 실제로 페이지를 빠져나간다.
  useEffect(() => {
    if (!hasImage) return;
    window.history.pushState({ rbgTrap: true }, "");
    const onPop = () => {
      if (history.current.length > 0) {
        undo();
        window.history.pushState({ rbgTrap: true }, "");
      } else {
        window.history.back();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasImage]);

  // ── 선택 영역 표시 (잘 보이게: 흰 외곽선 + 비비드 반투명 채움) ───────────────
  const renderView = () => {
    rafPending.current = false;
    const a = alphaMask.current, v = viewMask.current;
    if (!a || !v) return;
    const vc = v.getContext("2d")!;
    const rect = imgCanvas.current!.getBoundingClientRect();
    const px = rect.width > 0 ? v.width / rect.width : 1;   // 화면 1px → 캔버스 px
    const o = Math.max(2, Math.round(2.2 * px));            // 외곽선 두께(화면 기준 ~2px)
    vc.clearRect(0, 0, v.width, v.height);
    // 1) 흰 외곽 링: 8방향 오프셋으로 그린 뒤 중앙을 도려냄
    for (const [dx, dy] of [[-o, 0], [o, 0], [0, -o], [0, o], [-o, -o], [o, o], [-o, o], [o, -o]]) {
      vc.drawImage(a, dx, dy);
    }
    vc.globalCompositeOperation = "destination-out";
    vc.drawImage(a, 0, 0);
    vc.globalCompositeOperation = "source-over";
    // 2) 채움: 선택 영역을 비비드 색으로 틴트 후 반투명 합성
    const t = tintTmp.current!;
    const tc = t.getContext("2d")!;
    tc.clearRect(0, 0, t.width, t.height);
    tc.drawImage(a, 0, 0);
    tc.globalCompositeOperation = "source-in";
    tc.fillStyle = FILL;
    tc.fillRect(0, 0, t.width, t.height);
    tc.globalCompositeOperation = "source-over";
    vc.globalAlpha = 0.55;
    vc.drawImage(t, 0, 0);
    vc.globalAlpha = 1;
    // 3) 개체 선택 클릭 지점 마커
    for (const [x, y, pos] of segClicks.current) {
      const r = Math.max(5, 6 * px);
      vc.beginPath(); vc.arc(x, y, r, 0, Math.PI * 2);
      vc.fillStyle = pos ? "#22c55e" : "#ef4444"; vc.fill();
      vc.lineWidth = Math.max(2, 2 * px); vc.strokeStyle = "#fff"; vc.stroke();
    }
  };

  const scheduleRender = () => {
    if (rafPending.current) return;
    rafPending.current = true;
    requestAnimationFrame(renderView);
  };

  // ── 브러시 ──────────────────────────────────────────────────────────────────
  const canvasPt = (e: React.PointerEvent) => {
    const c = imgCanvas.current!;
    const r = c.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (c.width / r.width),
      y: (e.clientY - r.top) * (c.height / r.height),
    };
  };

  const strokeTo = (p: { x: number; y: number }) => {
    const scale = imgCanvas.current!.width / imgCanvas.current!.getBoundingClientRect().width;
    const rad = (brush / 2) * scale;
    const from = lastPt.current ?? p;
    for (const ctx of [
      maskCanvas.current!.getContext("2d")!,
      alphaMask.current!.getContext("2d")!,
    ]) {
      ctx.strokeStyle = "#fff"; ctx.fillStyle = "#fff";
      ctx.lineWidth = rad * 2; ctx.lineCap = "round"; ctx.lineJoin = "round";
      ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(p.x, p.y); ctx.stroke();
      ctx.beginPath(); ctx.arc(p.x, p.y, rad, 0, Math.PI * 2); ctx.fill();
    }
    lastPt.current = p;
    setMaskDirty(true);
    scheduleRender();
  };

  // ── 클릭 개체 선택 (InteractiveSeg / SAM2) ──────────────────────────────────
  const applyMaskImage = async (blob: Blob) => {
    // 서버 마스크 PNG → 전송용(black/white) + 표시용(투명/흰) 캔버스로 변환
    const img = await blobToImage(blob);
    const w = maskCanvas.current!.width, h = maskCanvas.current!.height;
    const tmp = document.createElement("canvas");
    tmp.width = w; tmp.height = h;
    const x = tmp.getContext("2d")!;
    x.drawImage(img, 0, 0, w, h);
    const src = x.getImageData(0, 0, w, h);
    const mOut = new ImageData(w, h), aOut = new ImageData(w, h);
    const d = src.data, md = mOut.data, ad = aOut.data;
    for (let i = 0; i < d.length; i += 4) {
      const on = d[i + 3] > 40 && d[i] + d[i + 1] + d[i + 2] > 60;
      md[i] = md[i + 1] = md[i + 2] = on ? 255 : 0; md[i + 3] = 255;
      if (on) { ad[i] = ad[i + 1] = ad[i + 2] = 255; ad[i + 3] = 255; }
    }
    maskCanvas.current!.getContext("2d")!.putImageData(mOut, 0, 0);
    alphaMask.current!.getContext("2d")!.putImageData(aOut, 0, 0);
    setMaskDirty(true);
    scheduleRender();
  };

  const segClick = async (p: { x: number; y: number }, positive: boolean) => {
    segClicks.current.push([Math.round(p.x), Math.round(p.y), positive ? 1 : 0]);
    setBusy("개체 인식 중…");
    try {
      const image = b64Of(imgCanvas.current!.toDataURL("image/png"));
      const r = await api.post("/removebg/segment",
        { image, clicks: segClicks.current }, { responseType: "blob", timeout: 0 });
      await applyMaskImage(r.data as Blob);
    } catch (e: any) {
      segClicks.current.pop();
      scheduleRender();
      toast.error("개체 인식 실패", e?.response?.data?.error?.message ?? e?.message ?? "");
    } finally { setBusy(null); }
  };

  // ── 확대/축소 제스처 ────────────────────────────────────────────────────────
  // 커서 아래 화면 지점(ax,ay)에 이미지 좌표(cx,cy)를 고정한 채 배율만 바꾼다.
  const startZoomGesture = (e: React.PointerEvent) => {
    const stage = stageRef.current;
    if (!stage) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const rect = stage.getBoundingClientRect();
    const s0 = view.scale;
    gesture.current = {
      mode: "zoom", startX: e.clientX, startY: e.clientY,
      scale0: s0, x0: view.x, y0: view.y,
      cx: (e.clientX - rect.left) / s0, cy: (e.clientY - rect.top) / s0,
      Lx: rect.left - view.x, Ly: rect.top - view.y,   // 레이아웃 기준 위치(제스처 동안 고정)
      ax: e.clientX, ay: e.clientY,                     // 화면상 앵커(시작 지점 고정)
    };
    setGesturing(true);
  };
  const startPanGesture = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    gesture.current = {
      mode: "pan", startX: e.clientX, startY: e.clientY,
      scale0: view.scale, x0: view.x, y0: view.y,
      cx: 0, cy: 0, Lx: 0, Ly: 0, ax: 0, ay: 0,
    };
    setGesturing(true);
  };
  const resetView = () => setView({ scale: 1, x: 0, y: 0 });

  // Ctrl+휠 확대/축소(브라우저 페이지 줌 대신) + Escape로 원래 크기 복귀.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const stage = stageRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const v = viewRef.current;
      const s2 = clampScale(v.scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
      if (s2 <= 1.0011) { setView({ scale: 1, x: 0, y: 0 }); return; }
      const cx = (e.clientX - rect.left) / v.scale, cy = (e.clientY - rect.top) / v.scale;
      const Lx = rect.left - v.x, Ly = rect.top - v.y;
      setView({ scale: s2, x: e.clientX - s2 * cx - Lx, y: e.clientY - s2 * cy - Ly });
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && viewRef.current.scale !== 1) resetView();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKey);
    return () => { el.removeEventListener("wheel", onWheel); window.removeEventListener("keydown", onKey); };
  }, []);

  // ── 포인터 ──────────────────────────────────────────────────────────────────
  const moveCursor = (e: React.PointerEvent) => {
    const ring = cursorRef.current;
    if (!ring) return;
    const r = viewMask.current!.getBoundingClientRect();
    const s = view.scale || 1;   // 링은 확대된 스테이지 내부 좌표 → 화면 오프셋을 배율로 나눔
    ring.style.transform = `translate(${(e.clientX - r.left) / s - brush / 2}px, ${(e.clientY - r.top) / s - brush / 2}px)`;
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (busy) return;
    // Ctrl+드래그 = 확대/축소, 중간버튼 또는 Ctrl+Shift+드래그 = 이동 (편집 도구보다 우선)
    if (e.ctrlKey && !e.shiftKey) { startZoomGesture(e); return; }
    if (e.button === 1 || (e.ctrlKey && e.shiftKey)) { startPanGesture(e); return; }
    const p = canvasPt(e);
    if (cropMode) {
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      cropStart.current = p;
      setCropDraft({ x: p.x, y: p.y, w: 0, h: 0 });
      return;
    }
    if (segMode) { segClick(p, !e.shiftKey); return; }
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drawing.current = true;
    lastPt.current = null;
    strokeAnchor.current = p;   // Shift 직선 그리기 기준점
    strokeTo(p);
  };

  // Shift 유지 시 시작점 기준으로 이동이 큰 축(가로/세로)에 스냅해 직선으로 칠한다.
  const constrainPt = (p: { x: number; y: number }, shift: boolean) => {
    const a = strokeAnchor.current;
    if (!shift || !a) return p;
    return Math.abs(p.x - a.x) >= Math.abs(p.y - a.y) ? { x: p.x, y: a.y } : { x: a.x, y: p.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (g) {
      if (g.mode === "zoom") {
        const s2 = clampScale(g.scale0 * Math.exp((g.startY - e.clientY) * 0.005)); // 위로 = 확대
        if (s2 <= 1.0011) setView({ scale: 1, x: 0, y: 0 });
        else setView({ scale: s2, x: g.ax - s2 * g.cx - g.Lx, y: g.ay - s2 * g.cy - g.Ly });
      } else {
        setView({ scale: g.scale0, x: g.x0 + (e.clientX - g.startX), y: g.y0 + (e.clientY - g.startY) });
      }
      return;
    }
    if (cropMode) {
      if (cropStart.current) {
        const p = canvasPt(e), s = cropStart.current;
        setCropDraft({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
      }
      return;
    }
    moveCursor(e);
    if (drawing.current && !busy && !segMode) strokeTo(constrainPt(canvasPt(e), e.shiftKey));
  };
  const onPointerUp = () => {
    if (gesture.current) { gesture.current = null; setGesturing(false); return; }
    drawing.current = false; lastPt.current = null; strokeAnchor.current = null; cropStart.current = null;
    if (cropMode) {
      if (cropDraft && cropDraft.w >= 8 && cropDraft.h >= 8) setCropRects(rs => [...rs, cropDraft]);
      setCropDraft(null);
    }
  };

  // ── 크롭(영역 자르기) ────────────────────────────────────────────────────────
  const toggleCrop = () => {
    setCropMode(m => {
      const next = !m;
      if (next) { setSegMode(false); clearMask(); }
      setCropRects([]); setCropDraft(null);
      return next;
    });
  };

  // 영역 1개만 선택했을 때 작업 이미지를 그 영역으로 잘라 교체(제자리 편집용).
  const applyCrop = (): boolean => {
    const c = imgCanvas.current!;
    if (cropRects.length !== 1) { toast.info("영역 한 개만 선택해 주세요", "여러 개는 「자르고 배경제거+저장」으로 각각 저장됩니다"); return false; }
    const r = cropRects[0];
    const sx = Math.max(0, Math.round(r.x)), sy = Math.max(0, Math.round(r.y));
    const sw = Math.min(c.width - sx, Math.round(r.w)), sh = Math.min(c.height - sy, Math.round(r.h));
    if (sw < 1 || sh < 1) return false;
    pushHistory(); // 자르기 전 전체 이미지를 되돌리기 스택에 저장
    const tmp = document.createElement("canvas");
    tmp.width = sw; tmp.height = sh;
    tmp.getContext("2d")!.drawImage(c, sx, sy, sw, sh, 0, 0, sw, sh);
    for (const ref of [imgCanvas, maskCanvas, viewMask]) { ref.current!.width = sw; ref.current!.height = sh; }
    ensureAux(sw, sh);
    c.getContext("2d")!.drawImage(tmp, 0, 0);
    clearMask();
    setCropMode(false); setCropRects([]); setCropDraft(null);
    refreshMeta();
    toast.success("자르기 완료", `${sw} × ${sh}px`);
    return true;
  };

  // 여러 영역을 각각: 잘라내기 → 배경 제거 → 여백 제거 → 개별 PNG 다운로드.
  // 작업 이미지는 그대로 두고 각 영역을 임시 캔버스로 추출해 처리한다.
  const cropBatchDownload = async () => {
    const c = imgCanvas.current!;
    const rects = cropRects;
    if (!rects.length) { toast.info("자를 영역을 드래그로 선택하세요", "여러 개 그릴 수 있습니다"); return; }
    let ok = 0;
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      const sx = Math.max(0, Math.round(r.x)), sy = Math.max(0, Math.round(r.y));
      const sw = Math.min(c.width - sx, Math.round(r.w)), sh = Math.min(c.height - sy, Math.round(r.h));
      if (sw < 1 || sh < 1) continue;
      setBusy(`배경 제거 중… (${i + 1}/${rects.length})`);
      try {
        const tmp = document.createElement("canvas");
        tmp.width = sw; tmp.height = sh;
        tmp.getContext("2d")!.drawImage(c, sx, sy, sw, sh, 0, 0, sw, sh);
        const image = b64Of(tmp.toDataURL("image/png"));
        const resp = await api.post("/removebg/remove", { image }, { responseType: "blob", timeout: 0 });
        const img = await blobToImage(resp.data as Blob);
        const rc = document.createElement("canvas");
        rc.width = img.naturalWidth; rc.height = img.naturalHeight;
        rc.getContext("2d")!.drawImage(img, 0, 0);
        await downloadCanvas(trimTransparent(rc), `${fileName}-crop${i + 1}.png`);
        ok++;
      } catch (e: any) {
        toast.error(`영역 ${i + 1} 처리 실패`, e?.response?.data?.error?.message ?? e?.message ?? "");
      }
    }
    setBusy(null);
    if (ok) toast.success("자르고 저장 완료", `${ok}/${rects.length}개 파일 다운로드`);
  };

  // ── AI 호출 ─────────────────────────────────────────────────────────────────
  const applyResult = async (blob: Blob) => {
    const img = await blobToImage(blob);
    const c = imgCanvas.current!;
    const ctx = c.getContext("2d")!;
    pushHistory();
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    maskCanvas.current!.width = c.width; maskCanvas.current!.height = c.height;
    viewMask.current!.width = c.width; viewMask.current!.height = c.height;
    ensureAux(c.width, c.height);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0);
    clearMask();
    refreshMeta();
  };

  const removeBg = async (): Promise<boolean> => {
    setBusy("배경 제거 중… (수십 초 걸릴 수 있습니다)");
    try {
      const image = b64Of(imgCanvas.current!.toDataURL("image/png"));
      const r = await api.post("/removebg/remove", { image }, { responseType: "blob", timeout: 0 });
      await applyResult(r.data as Blob);
      toast.success("배경 제거 완료");
      return true;
    } catch (e: any) {
      toast.error("배경 제거 실패", e?.response?.data?.error?.message ?? e?.message ?? "");
      return false;
    } finally { setBusy(null); }
  };

  const erase = async () => {
    if (!maskDirty) { toast.info("지울 영역을 먼저 선택하세요", "브러시로 칠하거나 개체 선택 모드에서 클릭"); return; }
    setBusy("선택 영역 지우는 중… (수십 초 걸릴 수 있습니다)");
    try {
      const image = b64Of(imgCanvas.current!.toDataURL("image/png"));
      const mask = b64Of(maskCanvas.current!.toDataURL("image/png"));
      const r = await api.post("/removebg/inpaint", { image, mask }, { responseType: "blob", timeout: 0 });
      await applyResult(r.data as Blob);
      toast.success("지우기 완료");
    } catch (e: any) {
      toast.error("지우기 실패", e?.response?.data?.error?.message ?? e?.message ?? "");
    } finally { setBusy(null); }
  };

  const runPlugin = async (name: string, busyMsg: string, okMsg: string, scale?: number) => {
    setBusy(busyMsg);
    try {
      const image = b64Of(imgCanvas.current!.toDataURL("image/png"));
      const r = await api.post("/removebg/plugin",
        { name, image, scale: scale ?? 2 }, { responseType: "blob", timeout: 0 });
      await applyResult(r.data as Blob);
      toast.success(okMsg);
    } catch (e: any) {
      toast.error(`${okMsg.replace(" 완료", "")} 실패`, e?.response?.data?.error?.message ?? e?.message ?? "");
    } finally { setBusy(null); }
  };

  const upscale = () => {
    const c = imgCanvas.current!;
    const s = Number(upScale);
    if (Math.max(c.width, c.height) * s > MAX_UPSCALE_OUT) {
      toast.error("이미지가 너무 큽니다", `업스케일 결과 긴 변이 ${MAX_UPSCALE_OUT}px를 넘습니다 — ×2를 사용하거나 더 작은 이미지로 시도하세요`);
      return;
    }
    runPlugin("RealESRGAN", `업스케일 ×${s} 진행 중… (수 분 걸릴 수 있습니다)`, "업스케일 완료", s);
  };

  // 작업 이미지를 여백(투명 가장자리) 제거 후 PNG로 저장.
  const download = () => { downloadCanvas(trimTransparent(imgCanvas.current!), `${fileName}-edited.png`); };

  // 배경 제거 → 저장을 한 번에.
  const removeBgAndDownload = async () => { if (await removeBg()) download(); };

  const reset = () => { setHasImage(false); setSegMode(false); setCropMode(false); setCropRects([]); setCropDraft(null); history.current = []; redoStack.current = []; setCanUndo(false); setCanRedo(false); setMaskDirty(false); setImgMeta(null); setView({ scale: 1, x: 0, y: 0 }); };

  // 작업 중인 이미지가 있어도, 새 이미지 파일을 끌어다 놓으면 바로 새로 시작.
  // (loadFile이 히스토리·마스크·캔버스를 모두 초기화하므로 별도 reset 불필요)
  const onPageDragOver = (e: React.DragEvent) => {
    if (Array.from(e.dataTransfer.types ?? []).includes("Files")) { e.preventDefault(); setDragOver(true); }
  };
  const onPageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;                       // 처리 중에는 무시
    const f = e.dataTransfer.files?.[0];
    if (f) loadFile(f);
  };

  const selBtn: React.CSSProperties = { fontSize: 12, padding: "4px 6px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--bg-1,#fff)", color: "var(--text-2)" };

  // ── 렌더 ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", position: "relative" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0, flexWrap: "wrap" }}>
        <span style={{ fontSize: 20 }}>🪄</span>
        <div style={{ marginRight: 6 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: "var(--text-1)", lineHeight: 1.2 }}>배경 제거 · 지우개 · 업스케일</div>
          <div style={{ fontSize: 10.5, color: "var(--text-3)" }}>IOPaint — RMBG-1.4 · big-lama · SAM2 · RealESRGAN · GFPGAN</div>
        </div>
        {svcReady === false && (
          <span style={{ fontSize: 11, color: "#ef4444", border: "1px solid #ef444440", borderRadius: 6, padding: "2px 8px" }}>
            AI 서비스 기동 중 — 잠시 후 새로고침
          </span>
        )}
        <div style={{ flex: 1 }} />
        {hasImage && (
          <>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-2)" }}
              title="[ 키로 작게 · ] 키로 크게">
              브러시 {brush}px
              <input type="range" min={8} max={120} value={brush} onChange={e => setBrush(Number(e.target.value))}
                style={{ width: 100, accentColor: "var(--p-500)" }} disabled={!!busy} />
            </label>
            <button className={`t-btn ${segMode ? "t-btn-primary" : ""}`} onClick={() => setSegMode(s => !s)}
              disabled={!!busy || !!cropMode || !has("InteractiveSeg")} title={offTitle("InteractiveSeg") ?? "클릭 한 번으로 개체 자동 선택 (Shift+클릭: 영역 제외)"}
              style={{ fontSize: 12 }}>🎯 개체 선택{segMode ? " ON" : ""}</button>
            <button className={`t-btn ${cropMode ? "t-btn-primary" : ""}`} onClick={toggleCrop}
              disabled={!!busy} title="영역을 드래그로 여러 개 선택할 수 있습니다"
              style={{ fontSize: 12 }}>⬚ 자르기{cropMode ? " ON" : ""}</button>
            {cropMode && (
              <>
                <span style={{ fontSize: 11.5, color: "var(--text-3)" }}>선택 {cropRects.length}개</span>
                <button className="t-btn" onClick={applyCrop} disabled={!!busy || cropRects.length !== 1} title="영역 1개를 작업 이미지에 잘라 적용(제자리 편집)" style={{ fontSize: 12 }}>✅ 자르기 적용</button>
                <button className="t-btn t-btn-primary" onClick={cropBatchDownload} disabled={!!busy || !cropRects.length}
                  title="각 영역을 잘라 → 배경 제거 → 여백 제거 → 개별 PNG로 저장" style={{ fontSize: 12 }}>✂️⬇ 자르고 배경제거+저장{cropRects.length ? ` (${cropRects.length})` : ""}</button>
                {cropRects.length > 0 && (
                  <button className="t-btn" onClick={() => { setCropRects([]); setCropDraft(null); }} disabled={!!busy} style={{ fontSize: 12 }}>영역 초기화</button>
                )}
              </>
            )}
            <button className="t-btn" onClick={clearMask} disabled={!!busy || !maskDirty} style={{ fontSize: 12 }}>선택 취소</button>
            <button className="t-btn t-btn-primary" onClick={erase} disabled={!!busy} style={{ fontSize: 12 }}>🧹 선택 영역 지우기</button>
            <button className="t-btn t-btn-primary" onClick={removeBg} disabled={!!busy} style={{ fontSize: 12 }}>✂️ 배경 제거</button>
            <button className="t-btn" onClick={removeBgAndDownload} disabled={!!busy} title="배경 제거 후 바로 PNG로 저장" style={{ fontSize: 12 }}>✂️⬇ 배경 제거+저장</button>
            <button className="t-btn" onClick={() => runPlugin("AnimeSeg", "애니 배경 제거 중…", "애니 배경 제거 완료")}
              disabled={!!busy || !has("AnimeSeg")} title={offTitle("AnimeSeg") ?? "애니/일러스트 전용 배경 제거"}
              style={{ fontSize: 12 }}>🎨 애니 배경</button>
            <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
              <button className="t-btn" onClick={upscale} disabled={!!busy || !has("RealESRGAN")}
                title={offTitle("RealESRGAN") ?? "RealESRGAN 업스케일"} style={{ fontSize: 12 }}>✨ 업스케일</button>
              <select value={upScale} onChange={e => setUpScale(e.target.value as "2" | "4")} disabled={!!busy} style={selBtn}>
                <option value="2">×2</option><option value="4">×4</option>
              </select>
            </span>
            <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
              <button className="t-btn" onClick={() => runPlugin(faceModel, "얼굴 보정 중…", "얼굴 보정 완료")}
                disabled={!!busy || !has(faceModel)} title={offTitle(faceModel) ?? "사진 속 얼굴 화질 복원"}
                style={{ fontSize: 12 }}>😊 얼굴 보정</button>
              <select value={faceModel} onChange={e => setFaceModel(e.target.value as "GFPGAN" | "RestoreFormer")} disabled={!!busy} style={selBtn}>
                <option value="GFPGAN">GFPGAN</option><option value="RestoreFormer">RestoreFormer</option>
              </select>
            </span>
            <span style={{ width: 1, height: 22, background: "var(--border)" }} />
            <button className="t-btn" onClick={undo} disabled={!!busy || !canUndo} style={{ fontSize: 12 }}>↩ 되돌리기</button>
            <button className="t-btn" onClick={redo} disabled={!!busy || !canRedo} style={{ fontSize: 12 }}>↪ 되돌리기 취소</button>
            <button className="t-btn" onClick={download} disabled={!!busy} style={{ fontSize: 12 }}>⬇ PNG 저장</button>
            <button className="t-btn" onClick={reset} disabled={!!busy} style={{ fontSize: 12 }}>새 이미지</button>
          </>
        )}
      </div>

      {/* Body */}
      <div
        ref={bodyRef}
        onDragOver={onPageDragOver}
        onDragLeave={() => setDragOver(false)}
        onDrop={onPageDrop}
        style={{ position: "relative", flex: 1, minHeight: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", padding: 18, ...(hasImage ? CHECKER : {}) }}>
        {/* 좌측 하단 — 해상도(화질) · 용량(사이즈) */}
        {hasImage && imgMeta && (
          <div style={{
            position: "absolute", left: 14, bottom: 14, zIndex: 9, pointerEvents: "none",
            display: "flex", alignItems: "center", gap: 8,
            background: "rgba(17,24,39,0.72)", color: "#fff", borderRadius: 8,
            padding: "5px 11px", fontSize: 11.5, fontWeight: 600, lineHeight: 1.3,
            boxShadow: "0 2px 10px rgba(0,0,0,0.32)",
          }}>
            <span title="해상도">🖼 {imgMeta.w.toLocaleString()} × {imgMeta.h.toLocaleString()} px</span>
            <span style={{ opacity: 0.35 }}>|</span>
            <span title="용량">💾 {fmtBytes(imgMeta.bytes)}</span>
          </div>
        )}
        {/* 확대 배율 표시 — 클릭 시 원래 크기(100%)로 복귀 */}
        {hasImage && view.scale !== 1 && (
          <button
            onClick={resetView}
            title="원래 크기로 (Esc)"
            style={{
              position: "absolute", left: "50%", bottom: 14, transform: "translateX(-50%)", zIndex: 9,
              display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
              background: "rgba(17,24,39,0.72)", color: "#fff", border: "none", borderRadius: 999,
              padding: "5px 13px", fontSize: 11.5, fontWeight: 700, lineHeight: 1.3,
              boxShadow: "0 2px 10px rgba(0,0,0,0.32)",
            }}>
            🔍 {Math.round(view.scale * 100)}% · 100%로 ⟲
          </button>
        )}
        {/* 업로드 존 — 이미지 없을 때 */}
        {!hasImage && (
          <div
            onClick={() => fileInput.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? "var(--p-500,#6366f1)" : "var(--border)"}`,
              borderRadius: 14, padding: "56px 48px", textAlign: "center", cursor: "pointer",
              background: dragOver ? "rgba(99,102,241,0.06)" : "var(--bg-1,#f8f9fa)",
              maxWidth: 560,
            }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🪄</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-1)" }}>이미지를 끌어다 놓거나 클릭해서 업로드</div>
            <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 8, lineHeight: 1.7 }}>
              ✂️ <b>배경 제거</b> — 클릭 한 번으로 피사체만 남기기 (🎨 애니/일러스트 전용 모델 지원)<br />
              🧹 <b>지우개</b> — 브러시로 칠하거나 🎯 <b>개체 선택</b>으로 클릭만 해서 AI가 자연스럽게 제거<br />
              ✨ <b>업스케일</b> ×2/×4 · 😊 <b>얼굴 보정</b> — 저화질 사진 복원<br />
              png · jpg · webp · bmp
            </div>
          </div>
        )}
        <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp,image/bmp" hidden
          onChange={e => { const f = e.target.files?.[0]; if (f) loadFile(f); e.target.value = ""; }} />

        {/* 작업 중 새 이미지를 드래그하면 표시되는 안내 오버레이 */}
        {hasImage && dragOver && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 10, pointerEvents: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(99,102,241,0.14)", border: "3px dashed var(--p-500,#6366f1)", borderRadius: 12,
          }}>
            <div style={{
              background: "var(--bg-0,#fff)", color: "var(--text-1)", fontSize: 14.5, fontWeight: 700,
              padding: "14px 24px", borderRadius: 12, boxShadow: "0 6px 26px rgba(0,0,0,0.22)",
            }}>🪄 여기에 놓으면 새 이미지로 시작합니다</div>
          </div>
        )}

        {/* 캔버스 스택 — 이미지 + 마스크 오버레이 (Ctrl+드래그/휠로 확대·축소) */}
        <div ref={stageRef} style={{
          position: "relative", display: hasImage ? "inline-block" : "none", maxWidth: "100%",
          boxShadow: "0 4px 24px rgba(0,0,0,0.18)", borderRadius: 6, overflow: "hidden",
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          transformOrigin: "0 0",
          transition: gesturing ? "none" : "transform 0.14s ease-out",
          willChange: "transform",
        }}>
          <canvas ref={imgCanvas} style={{ display: "block", maxWidth: "100%", maxHeight: "calc(100vh - 200px)", width: "auto", height: "auto" }} />
          <canvas ref={viewMask}
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
            onPointerEnter={() => { if (cursorRef.current) cursorRef.current.style.opacity = (segMode || cropMode) ? "0" : "1"; }}
            onPointerLeave={() => { if (cursorRef.current) cursorRef.current.style.opacity = "0"; }}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", cursor: busy ? "wait" : cropMode ? "crosshair" : segMode ? "pointer" : "none", touchAction: "none" }} />
          {/* 브러시 커서 링 — 현재 브러시 크기/위치 표시 */}
          <div ref={cursorRef} style={{
            position: "absolute", left: 0, top: 0, width: brush, height: brush, opacity: 0,
            border: "2px solid #fff", borderRadius: "50%", pointerEvents: "none",
            boxShadow: `0 0 0 1.5px ${FILL}, inset 0 0 0 1px rgba(0,0,0,0.35)`,
            background: "rgba(255,46,99,0.18)", transition: "opacity 0.15s",
          }} />
          {/* 크롭 영역들 — 확정된 여러 사각형(번호·삭제) + 드래그 중 점선 */}
          {cropMode && imgCanvas.current && cropRects.map((r, i) => {
            const cw = imgCanvas.current!.width, ch = imgCanvas.current!.height;
            return (
              <div key={i} style={{
                position: "absolute",
                left: `${(r.x / cw) * 100}%`, top: `${(r.y / ch) * 100}%`,
                width: `${(r.w / cw) * 100}%`, height: `${(r.h / ch) * 100}%`,
                border: "2px solid var(--p-400,#818cf8)", background: "rgba(99,102,241,0.12)",
                boxShadow: "0 0 0 1px rgba(0,0,0,0.4)", pointerEvents: "none",
              }}>
                <span style={{ position: "absolute", top: 2, left: 2, background: "var(--p-500,#6366f1)", color: "#fff", fontSize: 11, fontWeight: 700, borderRadius: 4, padding: "0 6px", lineHeight: "16px" }}>{i + 1}</span>
                <button onClick={() => setCropRects(rs => rs.filter((_, j) => j !== i))}
                  title="이 영역 삭제"
                  style={{ position: "absolute", top: -9, right: -9, width: 18, height: 18, borderRadius: "50%", background: "#ef4444", color: "#fff", border: "1.5px solid #fff", fontSize: 10, lineHeight: 1, cursor: "pointer", pointerEvents: "auto", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
              </div>
            );
          })}
          {cropMode && cropDraft && cropDraft.w > 1 && cropDraft.h > 1 && imgCanvas.current && (
            <div style={{
              position: "absolute",
              left: `${(cropDraft.x / imgCanvas.current.width) * 100}%`,
              top: `${(cropDraft.y / imgCanvas.current.height) * 100}%`,
              width: `${(cropDraft.w / imgCanvas.current.width) * 100}%`,
              height: `${(cropDraft.h / imgCanvas.current.height) * 100}%`,
              border: "2px dashed #fff", background: "rgba(255,255,255,0.12)",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.5)", pointerEvents: "none",
            }} />
          )}
          {/* 마스크 캔버스(전송용)는 화면에 표시하지 않음 */}
          <canvas ref={maskCanvas} style={{ display: "none" }} />
          {busy && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, border: "3px solid rgba(255,255,255,0.25)", borderTopColor: "#fff", borderRadius: "50%", animation: "rbg-spin 0.9s linear infinite" }} />
              <div style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>{busy}</div>
            </div>
          )}
        </div>
      </div>

      {hasImage && (
        <div style={{ padding: "6px 16px", borderTop: "1px solid var(--border)", fontSize: 11.5, color: "var(--text-3)", flexShrink: 0 }}>
          {cropMode
            ? "⬚ 자르기 모드 — 드래그로 영역을 여러 개 그릴 수 있습니다(각 영역 ✕로 삭제). 「✂️⬇ 자르고 배경제거+저장」을 누르면 각 영역을 잘라 배경 제거·여백 제거 후 개별 PNG로 저장합니다. 영역 1개만 있을 땐 「✅ 자르기 적용」으로 제자리 편집도 가능."
            : segMode
            ? "🎯 개체 선택 모드 — 지우고 싶은 개체를 클릭하면 AI가 자동 선택합니다. 클릭을 더하면 정교해지고, Shift+클릭은 영역을 제외합니다. 선택 후 「선택 영역 지우기」."
            : "🖌 이미지 위를 칠해 지울 영역을 선택하고 「선택 영역 지우기」, 또는 바로 「배경 제거」를 누르세요. 🎯 개체 선택을 켜면 클릭만으로 선택됩니다.  브러시 크기는 [ · ] 키로 조절, Shift+드래그로 수직·수평 직선.  🔍 Ctrl+드래그(상하) 또는 Ctrl+휠로 확대/축소, 중간버튼·Ctrl+Shift+드래그로 이동, Esc로 원래 크기."}
        </div>
      )}

      {/* 우측 하단 — remove.bg(외부 서비스) 새창 바로가기 */}
      <a
        href="https://www.remove.bg/ko"
        target="_blank"
        rel="noopener noreferrer"
        title="remove.bg 새 창으로 열기"
        style={{
          // 우측 하단의 외관 설정 버튼(40px, right:16/bottom:16)과 겹치지 않게 왼쪽으로 비켜 배치
          position: "absolute", right: 68, bottom: 18, zIndex: 30,
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "8px 14px", borderRadius: 999,
          background: "var(--p-600,#4f46e5)", color: "#fff",
          fontSize: 12.5, fontWeight: 700, textDecoration: "none",
          boxShadow: "0 4px 16px rgba(0,0,0,0.28)",
        }}
        onMouseEnter={e => (e.currentTarget.style.background = "var(--p-500,#6366f1)")}
        onMouseLeave={e => (e.currentTarget.style.background = "var(--p-600,#4f46e5)")}
      >
        remove.bg <span style={{ fontSize: 13 }}>↗</span>
      </a>

      <style>{`@keyframes rbg-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
